if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');

        const loadingSpinner = this.querySelector('.loading__spinner');
        if (loadingSpinner) loadingSpinner.classList.remove('hidden');

        const formData = new FormData(this.form);
        const selectedVariantId = Number(formData.get('id'));
        const selectedQuantity = Number(formData.get('quantity') || 1);

        /*
         * The optional greaser block sits outside the product form,
         * so locate it within the current product section.
         */
        const productInfo = this.closest('product-info');
        const greaserAddon = productInfo
          ? productInfo.querySelector('[data-bar-greaser-addon]')
          : null;

        const greaserCheckbox = greaserAddon
          ? greaserAddon.querySelector('[data-bar-greaser-checkbox]')
          : null;

        const addGreaser =
          greaserAddon &&
          greaserCheckbox &&
          greaserCheckbox.checked &&
          !greaserCheckbox.disabled;

        const greaserVariantId = addGreaser
          ? Number(greaserAddon.dataset.accessoryVariantId)
          : null;

        let config;

        /*
         * When the greaser is selected, submit both products together
         * through Shopify's multi-item cart request.
         */
        if (addGreaser && greaserVariantId) {
          config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.headers['Content-Type'] = 'application/json';

          const items = [
            {
              id: selectedVariantId,
              quantity: selectedQuantity,
            },
            {
              id: greaserVariantId,
              quantity: 1,
            },
          ];

          /*
           * Preserve line-item properties from the original product form,
           * if any are used on a product.
           */
          const properties = {};

          for (const [key, value] of formData.entries()) {
            const propertyMatch = key.match(/^properties\[(.+)\]$/);

            if (propertyMatch && value !== '') {
              properties[propertyMatch[1]] = value;
            }
          }

          if (Object.keys(properties).length > 0) {
            items[0].properties = properties;
          }

          const sellingPlan = formData.get('selling_plan');

          if (sellingPlan) {
            items[0].selling_plan = Number(sellingPlan);
          }

          const requestBody = {
            items,
          };

          if (this.cart) {
            requestBody.sections = this.cart
              .getSectionsToRender()
              .map((section) => section.id);

            requestBody.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }

          config.body = JSON.stringify(requestBody);
        } else {
          /*
           * Standard Shopify behaviour when the greaser is not selected.
           */
          config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );

            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }

          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: selectedVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(
                response.description ||
                  response.message ||
                  'Unable to add this item to the cart.'
              );

              const soldOutMessage =
                this.submitButton.querySelector('.sold-out-message');

              if (soldOutMessage) {
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }

              this.error = true;
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker(
              'add:wait-for-subscribers'
            );

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: selectedVariantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  'add:wait-for-subscribers',
                  startMarker
                );
              });
            }

            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      'add:paint-updated-sections',
                      () => {
                        this.cart.renderContents(response);
                      }
                    );
                  });
                },
                { once: true }
              );

              quickAddModal.hide(true);
            } else {
              CartPerformance.measure(
                'add:paint-updated-sections',
                () => {
                  this.cart.renderContents(response);
                }
              );
            }

            /*
             * Reset the accessory checkbox after a successful add.
             */
            if (addGreaser && greaserCheckbox) {
              greaserCheckbox.checked = false;
            }
          })
          .catch((error) => {
            console.error(error);

            this.error = true;
            this.handleErrorMessage(
              'There was a problem adding the items to your cart. Please try again.'
            );
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');

            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }

            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }

            if (loadingSpinner) {
              loadingSpinner.classList.add('hidden');
            }

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');

          if (text) {
            this.submitButtonText.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent =
            window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}